import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import Header from './components/Header.jsx';
import OptionCard from './components/OptionCard.jsx';
import ManagePdfPanel from './components/ManagePdfPanel.jsx';
import ExtractAIPanel from './components/ExtractAIPanel.jsx';

// Rango unicode de los diacríticos combinables (tildes, acentos, etc.).
// Se usa como string para mantener el código fuente en ASCII.
const DIACRITICS_REGEX = new RegExp('[\\u0300-\\u036f]', 'g');

// Nombre con el que se muestra/descarga el programa. Debe coincidir con el
// nombre que arma el backend al descargar (ver courses_info.js: download-url).
// Se normaliza a minúsculas, sin tildes ni caracteres especiales, y con los
// espacios/símbolos reemplazados por "_".
const buildProgramFileName = courseName => {
  const normalized = String(courseName || 'del_curso')
    .normalize('NFD') // separa los diacríticos de las letras
    .replace(DIACRITICS_REGEX, '') // elimina tildes y diacríticos
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_') // cualquier símbolo/espacio -> "_"
    .replace(/^_+|_+$/g, ''); // sin "_" al inicio o al final
  return `programa_${normalized || 'del_curso'}.pdf`;
};

// El backend guarda la key del programa como
// `course-programs/{courseId}/{timestamp}-{nombreOriginal}` (ver courses_info.js).
// De esa ruta derivamos la fecha de subida a partir del timestamp.
const parseProgramUploadDate = bucketPath => {
  const base = (bucketPath || '').split('/').pop() || '';
  const dashIndex = base.indexOf('-');
  const rawTimestamp = base.slice(0, dashIndex);
  const hasTimestamp = dashIndex > 0 && /^\d+$/.test(rawTimestamp);

  const uploadedAt = hasTimestamp ? new Date(Number(rawTimestamp)) : null;

  return uploadedAt && !Number.isNaN(uploadedAt.getTime())
    ? `Subido el ${uploadedAt.toLocaleDateString('es-CL')}`
    : null;
};

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: '#ffffff',
  borderRadius: 2,
  borderColor: 'primary.main',
  borderWidth: 2,
  borderStyle: 'solid',
  boxShadow: 24,
  overflow: 'hidden',
  width: '92vw',
  maxWidth: 640,
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
};

// Modal de "Programa del curso": permite gestionar el PDF del programa
// (subir / reemplazar / eliminar) y extraer su contenido con IA.
//
// Props:
// - open / setOpen: control de apertura del modal.
// - course: curso seleccionado (name, year, semester, programBucketPath...).
const CourseProgramModal = ({ open, setOpen, course = {} }) => {
  // Pestaña activa: 'manage' | 'extract'.
  const [activeTab, setActiveTab] = useState('manage');

  const handleClose = () => setOpen(false);

  const hasProgram = Boolean((course.programBucketPath || '').trim());

  // Nombre mostrado (igual al de descarga) y fecha real de subida.
  const fileName = buildProgramFileName(course.name);
  const fileMeta = parseProgramUploadDate(course.programBucketPath);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby='course-program-title'
      aria-describedby='course-program-description'>
      <Box sx={modalStyle}>
        <Header course={course} onClose={handleClose} />

        <div className='flex-1 overflow-y-auto p-6'>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <OptionCard
              icon={<DescriptionOutlinedIcon />}
              title='Gestionar PDF'
              description='Sube, reemplaza o elimina el archivo de programa del curso'
              accent='primary'
              active={activeTab === 'manage'}
              badge={hasProgram ? 'Archivo cargado' : undefined}
              onClick={() => setActiveTab('manage')}
            />
            <OptionCard
              icon={<AutoAwesomeIcon />}
              title='Extraer con IA'
              description='Analiza el programa con IA y guarda el contenido clave en la base de datos'
              accent='secondary'
              active={activeTab === 'extract'}
              onClick={() => setActiveTab('extract')}
            />
          </div>

          <div className='mt-6 border-t border-primary/10 pt-6'>
            {activeTab === 'manage' ? (
              <ManagePdfPanel
                courseId={course.id}
                hasProgram={hasProgram}
                fileName={fileName}
                fileMeta={fileMeta}
              />
            ) : (
              <ExtractAIPanel
                courseId={course.id}
                programContent={course.program_content}
                disabled={!hasProgram}
              />
            )}
          </div>
        </div>

        <div className='flex justify-end border-t border-primary/10 px-6 py-4'>
          <button
            type='button'
            onClick={handleClose}
            className='rounded-full bg-muted px-6 py-2 font-semibold text-primary transition duration-150 hover:bg-muted-foreground/20'>
            Cancelar
          </button>
        </div>
      </Box>
    </Modal>
  );
};

export default CourseProgramModal;
